import React, { useEffect, useMemo, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { FormikHelpers, FormikValues, useFormikContext } from 'formik';

import {
	Button,
	StylovyzeComposableDialog,
	StylovyzeFormV2,
	InputText,
	InputAutocomplete,
	InputSelect,
	Text,
	decoratedAttributes,
} from '@innovyze/stylovyze';

import { useGlobalization } from '@Translations/useGlobalization';
import { listGroupSets, listAllFailureDefinitions, listDeteriorationModels } from '@Actions';
import { selectAllFailureDefinitions, selectGroupSets, selectDeteriorationModelsCards } from '@Selectors';
import { DETERIORATION_PICKER_RULE, usePickerOptions } from '@Hooks/useSystemTypes';
import { PickerStatus } from '@Components/Utils/PickerStatus';
import { FailureDefinition } from '@Types';
import { isAlphanumericName, isBlankName, isDuplicateName } from '@Utils/nameValidation';
import { toGroupSetAssetType, toGroupSetSystemType } from '@Utils/rehabSystemTypes';

interface CreateDeteriorationModelDialogProps {
	isOpen: boolean;
	setOpen: (arg0: boolean) => void;
	onCreate: (request: { modelName: string; assetGroupSet: string; failureDefinition: string | null }) => Promise<void>;
}

interface CreateDMFormValues {
	systemType: string;
	assetType: string;
	assetGroupSet: string;
	modelName: string;
	failureDefinition: string;
}

const RequiredMark = () => <span style={{ color: '#dc2626', marginLeft: 2 }}>*</span>;

const AssetTypeField = () => {
	const { t } = useGlobalization();
	const { values, errors, touched, setFieldValue } = useFormikContext<CreateDMFormValues>();
	const { assetTypesFor } = usePickerOptions(DETERIORATION_PICKER_RULE);
	const prevSystemType = useRef(values.systemType);

	useEffect(() => {
		if (prevSystemType.current && prevSystemType.current !== values.systemType) {
			setFieldValue('assetType', '');
			setFieldValue('assetGroupSet', '');
			setFieldValue('failureDefinition', '');
		}
		prevSystemType.current = values.systemType;
	}, [values.systemType, setFieldValue]);

	const assetTypes = values.systemType ? assetTypesFor(values.systemType) : undefined;

	const hasError = !!touched.assetType && !!errors.assetType;

	return (
		<InputSelect
			menuItems={
				assetTypes
					? assetTypes
							.filter(at => at.displayName === 'Pipe')
							.map(at => ({
								key: at.id,
								value: at.id,
								label: t(at.displayName),
							}))
					: []
			}
			fieldAttrs={{ name: 'assetType' }}
			selectProps={{
				label: (
					<>
						{t('Asset Type')}
						<RequiredMark />
					</>
				) as unknown as string,
				fullWidth: true,
				disabled: !values.systemType,
				error: hasError,
			}}
			helperText={hasError ? String(errors.assetType) : ''}
		/>
	);
};

const GroupSetField = () => {
	const { t } = useGlobalization();
	const { values, errors, touched, setFieldValue } = useFormikContext<CreateDMFormValues>();
	const groupSets = selectGroupSets();
	const prevAssetType = useRef(values.assetType);

	useEffect(() => {
		if (prevAssetType.current && prevAssetType.current !== values.assetType) {
			setFieldValue('assetGroupSet', '');
			setFieldValue('failureDefinition', '');
		}
		prevAssetType.current = values.assetType;
	}, [values.assetType, setFieldValue]);

	const isDisabled = !values.systemType || !values.assetType;

	const options = useMemo(
		() =>
			groupSets
				.filter(
					gs =>
						gs.publishState === 'published' &&
						(!values.systemType || gs.systemType === toGroupSetSystemType(values.systemType)) &&
						(!values.assetType || gs.assetType === toGroupSetAssetType(values.assetType)),
				)
				.map(gs => ({ key: gs._id, value: gs.name })),
		[groupSets, values.systemType, values.assetType],
	);

	const hasError = !!touched.assetGroupSet && !!errors.assetGroupSet;

	return (
		<InputAutocomplete
			options={options}
			fieldAttrs={{ name: 'assetGroupSet' }}
			label={
				(
					<>
						{t('Asset Group Set')}
						<RequiredMark />
					</>
				) as unknown as string
			}
			textFieldProps={{ fullWidth: true, disabled: isDisabled }}
			error={hasError}
			helperText={
				hasError ? String(errors.assetGroupSet) : t('Only published sets appear here. Go to Groups section to publish.')
			}
		/>
	);
};

const FailureDefinitionField = () => {
	const { t } = useGlobalization();
	const { values, errors, touched } = useFormikContext<CreateDMFormValues>();
	const failureDefinitions = selectAllFailureDefinitions();

	const options = useMemo(() => {
		if (!values.systemType || !values.assetType) return [];
		return failureDefinitions
			.filter((fd: FailureDefinition) => fd.systemType === values.systemType && fd.assetType === values.assetType)
			.map(fd => ({ key: fd.id, value: fd.name }));
	}, [failureDefinitions, values.systemType, values.assetType]);

	const isDisabled = !values.systemType || !values.assetType;
	const hasError = !!touched.failureDefinition && !!errors.failureDefinition;

	return (
		<InputAutocomplete
			options={options}
			fieldAttrs={{ name: 'failureDefinition' }}
			label={t('Failure Definition')}
			textFieldProps={{ fullWidth: true, disabled: isDisabled }}
			error={hasError}
			helperText={
				hasError
					? String(errors.failureDefinition)
					: isDisabled
					? t('Please select a System Type and Asset Type first.')
					: options.length === 0
					? t('No failure definitions found that match the selected system and asset type.')
					: t('Optional: Use your inspection or event data to generate more accurate survival curves')
			}
		/>
	);
};

export const CreateDeteriorationModelDialog = ({ setOpen, isOpen, onCreate }: CreateDeteriorationModelDialogProps) => {
	const { t } = useGlobalization();
	const dispatch = useDispatch();
	const history = useHistory();
	const { systemTypes, status: pickerStatus, retry: retryPicker } = usePickerOptions(DETERIORATION_PICKER_RULE);
	const deteriorationModelsCards = selectDeteriorationModelsCards();

	useEffect(() => {
		dispatch(listGroupSets());
		dispatch(listAllFailureDefinitions());
		dispatch(
			listDeteriorationModels({
				page: 1,
				slice: 1000,
				sortKey: 'updated_at',
				sortDescending: true,
			}),
		);
	}, []);

	const groupSets = selectGroupSets();

	const handleSubmit = async (values: FormikValues, { setSubmitting, setFieldError }: FormikHelpers<FormikValues>) => {
		setSubmitting(true);
		try {
			await onCreate({
				modelName: values.modelName.trim(),
				assetGroupSet: values.assetGroupSet,
				failureDefinition: values.failureDefinition === '' ? null : values.failureDefinition,
			});
		} catch (e) {
			if (e instanceof Error && (e as Error & { isDuplicateName?: boolean }).isDuplicateName) {
				setFieldError('modelName', e.message);
			}
			setSubmitting(false);
		}
	};

	if (groupSets.length === 0) {
		return (
			<StylovyzeComposableDialog
				onCancel={() => setOpen(false)}
				open={isOpen}
				title={t('There are no Asset Group Sets defined')}
				footer={
					<>
						<Button variant="contained" color="secondary" onClick={() => setOpen(false)} sx={{ borderRadius: '2px' }}>
							{t('Close')}
						</Button>
						<Button
							variant="contained"
							onClick={() => history.push('/agsets')}
							sx={{ borderRadius: '2px', backgroundColor: '#0696D7', '&:hover': { backgroundColor: '#0580b9' } }}>
							{t('Create Asset Group Set')}
						</Button>
					</>
				}>
				<Text>
					{t(
						'To create a Deterioration Model, you first need an Asset Group Set. ' +
							'Groups let you organize assets with similar characteristics so you can assign the right survival curves to each.',
					)}
				</Text>
			</StylovyzeComposableDialog>
		);
	}

	return (
		<StylovyzeFormV2
			initialValues={{
				systemType: '',
				assetType: '',
				assetGroupSet: '',
				modelName: '',
				failureDefinition: '',
			}}
			validate={values => {
				const errors: { [key: string]: string } = {};
				if (isBlankName(values.modelName)) errors.modelName = t('Required');
				else if (!isAlphanumericName(values.modelName)) {
					errors.modelName = t('Only alphanumeric characters are allowed');
				} else if (
					isDuplicateName(
						values.modelName,
						deteriorationModelsCards.cards.map(card => card.name),
					)
				) {
					errors.modelName = t('Enter a unique name');
				}
				if (!values.systemType) errors.systemType = t('Required');
				if (!values.assetType) errors.assetType = t('Required');
				if (!values.assetGroupSet) errors.assetGroupSet = t('Required');
				return errors;
			}}
			onSubmit={handleSubmit}>
			{({ submitForm, isSubmitting, errors, touched, values }) => {
				const adpAttrs = decoratedAttributes({
					modelname: values.modelName ?? '',
					assetgroupset: values.assetGroupSet ?? '',
					failuredefinition: values.failureDefinition ?? '',
				});

				return (
					<StylovyzeComposableDialog
						onCancel={() => setOpen(false)}
						open={isOpen}
						title={t('New Deterioration Model')}
						footer={
							<>
								<Button
									variant="contained"
									color="secondary"
									onClick={() => setOpen(false)}
									sx={{ borderRadius: '2px' }}>
									{t('Cancel')}
								</Button>
								<Button
									variant="contained"
									disabled={isSubmitting}
									onClick={submitForm}
									{...adpAttrs}
									adp-click="true"
									adp-component-id="create-deterioration-model-confirm"
									adp-component-name="Create Deterioration Model"
									sx={{ borderRadius: '2px', backgroundColor: '#0696D7', '&:hover': { backgroundColor: '#0580b9' } }}>
									{t('Create Deterioration Model')}
								</Button>
							</>
						}>
						<InputText
							fieldAttrs={{ name: 'modelName' }}
							textField={{
								label: (
									<>
										{t('Model Name')}
										<RequiredMark />
									</>
								) as unknown as string,
								fullWidth: true,
								error: !!touched.modelName && !!errors.modelName,
								helperText: touched.modelName && errors.modelName ? errors.modelName : '',
							}}
						/>
						<InputSelect
							menuItems={systemTypes.map(st => ({ key: st.id, value: st.id, label: t(st.displayName) }))}
							fieldAttrs={{ name: 'systemType' }}
							selectProps={{
								label: (
									<>
										{t('System Type')}
										<RequiredMark />
									</>
								) as unknown as string,
								fullWidth: true,
								error: !!touched.systemType && !!errors.systemType,
							}}
							helperText={touched.systemType && errors.systemType ? errors.systemType.toString() : ''}
						/>
						<PickerStatus status={pickerStatus} retry={retryPicker} />
						<AssetTypeField />
						<GroupSetField />
						<FailureDefinitionField />
					</StylovyzeComposableDialog>
				);
			}}
		</StylovyzeFormV2>
	);
};
